import { useRef, useState } from 'react';

import { useParams } from '@tanstack/react-router';

import { contactsApi } from '@/api/queries/contacts';
import { client } from '@/api/generated/client.gen';
import { withForm } from '@/components/form';
import { Button, Icon, IconButton, Select, SectionCard, Tag, useToast } from '@/components/ui';

import { defaultFormValues } from '../../contactSchema';

// Free-text category stored on the attachment; the list groups nothing, but the
// chosen value lets you tell CVs / letters / cover photos / photos apart.
const CATEGORY_OPTIONS = [
  { value: 'photo', label: 'Photo' },
  { value: 'cover', label: 'Cover photo' },
  { value: 'cv', label: 'CV' },
  { value: 'letter', label: 'Letter' },
  { value: 'other', label: 'Other' },
];

function formatBytes(size: number | undefined): string | undefined {
  if (size === undefined || size < 0) return undefined;
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = size / 1024;
  let unit = units[0];
  for (let i = 1; i < units.length && value >= 1024; i++) {
    value /= 1024;
    unit = units[i];
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${unit}`;
}

export const AttachmentsSection = withForm({
  defaultValues: defaultFormValues,
  render: function AttachmentsSection({ form }) {
    const params = useParams({ strict: false }) as { id?: string };
    const contactId = params.id;
    const toast = useToast();
    const upload = contactsApi.useUploadAttachment();
    const removeAttachment = contactsApi.useDeleteAttachment();
    const fileRef = useRef<HTMLInputElement>(null);
    const [category, setCategory] = useState('photo');

    const acceptForCategory =
      category === 'photo' || category === 'cover' ? 'image/*' : undefined;

    const handleFiles = async (files: FileList | null) => {
      if (!files || !contactId) return;
      // Upload sequentially so a series of photos lands in order.
      for (const file of Array.from(files)) {
        try {
          const att = await upload.mutateAsync({ id: contactId, file, category });
          form.pushFieldValue('attachments', att as never);
        } catch {
          toast.show({ title: `Failed to upload ${file.name}`, severity: 'error' });
        }
      }
      if (fileRef.current) fileRef.current.value = '';
    };

    const handleRemove = async (index: number, fileId?: string) => {
      if (contactId && fileId) {
        try {
          await removeAttachment.mutateAsync({ id: contactId, fileId });
        } catch {
          toast.show({ title: 'Failed to remove attachment', severity: 'error' });
          return;
        }
      }
      form.removeFieldValue('attachments', index);
    };

    const handleDownload = async (fileId?: string, name?: string) => {
      if (!contactId || !fileId) return;
      try {
        // The API redirects to a short-lived presigned URL; fetch as a blob so the
        // auth headers are applied, then hand it to the browser.
        const res = await client.instance.get(`/contacts/${contactId}/attachments/${fileId}`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name || fileId;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        toast.show({ title: 'Could not open attachment', severity: 'error' });
      }
    };

    const busy = upload.isPending;

    return (
      <SectionCard
        title="Attachments"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 150 }}>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                options={CATEGORY_OPTIONS}
                aria-label="Attachment category"
              />
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={acceptForCategory}
              style={{ display: 'none' }}
              onChange={(e) => void handleFiles(e.target.files)}
            />
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon name="upload" size={13} />}
              loading={busy}
              disabled={!contactId || busy}
              onClick={() => fileRef.current?.click()}
            >
              Upload
            </Button>
          </div>
        }
      >
        {!contactId && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
            Save the contact first, then you can upload files.
          </div>
        )}
        <form.AppField name="attachments">
          {(field) => {
            const items = field.state.value ?? [];
            if (items.length === 0) {
              return (
                <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>
                  No attachments yet. Pick a category and upload CVs, letters, cover photos or
                  photos — upload as many as you like.
                </div>
              );
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((a, i) => {
                  const sizeLabel = formatBytes(a.size);
                  const isImage = a.type?.startsWith('image/');
                  return (
                    <div
                      key={a.fileId || i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 10,
                        border: '1px solid var(--divider)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface-2)',
                      }}
                    >
                      <Icon
                        name={isImage ? 'eye' : 'note'}
                        size={16}
                        style={{ color: 'var(--text-3)' }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            color: 'var(--text)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={a.name}
                        >
                          {a.name || a.fileId}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                          {[a.type, sizeLabel, a.uploadedBy && `by ${a.uploadedBy}`]
                            .filter(Boolean)
                            .join(' · ')}
                          {a.uploadedAt
                            ? ` · ${new Date(a.uploadedAt).toLocaleDateString()}`
                            : ''}
                        </div>
                      </div>
                      {a.category && <Tag>{a.category}</Tag>}
                      <IconButton
                        icon="download"
                        iconSize={12}
                        size="sm"
                        label="Download attachment"
                        onClick={() => void handleDownload(a.fileId, a.name)}
                      />
                      <IconButton
                        icon="trash"
                        iconSize={12}
                        size="sm"
                        variant="danger"
                        label="Remove attachment"
                        onClick={() => void handleRemove(i, a.fileId)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          }}
        </form.AppField>
      </SectionCard>
    );
  },
});
